import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import SupplierForm from './SupplierForm';
import Layout from '../../components/Layout';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import SlideOver from '../../components/ui/SlideOver';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Edit, Trash2, Eye } from 'lucide-react';

const Suppliers = () => {
  const navigate = useNavigate();
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, [pagination.page, search, activeFilter]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/suppliers', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search,
          is_active: activeFilter
        }
      });
      
      setSuppliers(response.data.suppliers);
      setPagination(prev => ({
        ...prev,
        ...response.data.pagination
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load suppliers');
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!confirm('Are you sure you want to deactivate this supplier?')) return;

    try {
      await api.delete(`/suppliers/${supplierId}`);
      fetchSuppliers(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete supplier');
      console.error('Delete error:', err);
    }
  };

  return (
    <TooltipProvider>
      <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Suppliers
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your supplier database
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
              >
                + New Supplier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <FormLabel>Search</FormLabel>
              <Input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Search by name, code, or email..."
                className="h-8"
              />
            </div>
            <div>
              <FormLabel>Status</FormLabel>
              <Select
                value={activeFilter}
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </div>
          </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Suppliers Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-body text-[var(--ds-text-muted)]">Loading suppliers...</p>
            </div>
          ) : suppliers.length === 0 ? (
            <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
              <p>No suppliers found</p>
              <Button
                variant="primary"
                onClick={() => setShowCreateModal(true)}
                className="mt-4"
              >
                Create your first supplier
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead className="text-right">Current Balance</TableHead>
                    <TableHead className="text-right">Available Balance</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell className="whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-[rgba(34,197,94,0.20)] flex items-center justify-center text-[var(--ds-status-success)] font-bold text-[12px]">
                                {supplier.supplier_name.charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-[12px] font-medium text-[var(--ds-text-primary)]">
                                {supplier.supplier_name}
                              </div>
                              {supplier.supplier_code && (
                                <div className="text-[12px] text-[var(--ds-text-muted)]">
                                  Code: {supplier.supplier_code}
                                </div>
                              )}
                            </div>
                          </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {supplier.email && (
                          <div className="text-[12px] text-[var(--ds-text-primary)]">{supplier.email}</div>
                        )}
                        {supplier.contact_name && (
                          <div className="text-[12px] text-[var(--ds-text-muted)]">{supplier.contact_name}</div>
                        )}
                        {supplier.phone && (
                          <div className="text-[12px] text-[var(--ds-text-muted)]">{supplier.phone}</div>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-[12px] text-[var(--ds-text-primary)]">
                        {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(parseFloat(supplier.current_balance ?? 0))}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-[12px] text-[var(--ds-text-primary)]">
                        {supplier.credit_limit != null && supplier.credit_limit !== ''
                          ? (() => {
                              const creditLimit = parseFloat(supplier.credit_limit ?? 0) || 0;
                              const currentBalance = parseFloat(supplier.current_balance ?? 0) || 0;
                              const available = creditLimit - Math.abs(currentBalance);
                              return new Intl.NumberFormat('en-GB', {
                                style: 'currency',
                                currency: 'GBP',
                              }).format(available);
                            })()
                          : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-[12px] font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <Tooltip content="View">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/suppliers/${supplier.id}`)}
                                className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Edit">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => {
                                  setSelectedSupplier(supplier);
                                  setShowEditModal(true);
                                }}
                                className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Deactivate">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleDeleteSupplier(supplier.id)}
                                disabled={!supplier.is_active}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Card>
                  <CardContent className="px-4 py-3 sm:px-6 border-t border-[var(--ds-border-divider)]">
                  <div className="flex items-center justify-between">
                    <div className="text-body text-[var(--ds-text-secondary)]">
                      Showing page <span className="font-medium">{pagination.page}</span> of{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                      {' '}({pagination.total} total suppliers)
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Create Slide-Over */}
      <SlideOver
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        title="New Supplier"
        width="w-full max-w-2xl"
      >
        <SupplierForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchSuppliers();
          }}
        />
      </SlideOver>

      {/* Edit Slide-Over */}
      {selectedSupplier && (
        <SlideOver
          open={showEditModal}
          onOpenChange={(open) => {
            setShowEditModal(open);
            if (!open) setSelectedSupplier(null);
          }}
          title="Edit Supplier"
          width="w-full max-w-2xl"
        >
          <SupplierForm
            supplier={selectedSupplier}
            onClose={() => {
              setShowEditModal(false);
              setSelectedSupplier(null);
            }}
            onSuccess={() => {
              setShowEditModal(false);
              setSelectedSupplier(null);
              fetchSuppliers();
            }}
          />
        </SlideOver>
      )}

      </Layout>
    </TooltipProvider>
  );
};

export default Suppliers;

